import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import axios from 'axios'
import api from '../services/api'
import { Project, ProjectDisplay, toProjectDisplay, OperationResult } from '../types/project'

interface ProjectsContextType {
  projects: ProjectDisplay[]
  isLoading: boolean
  error: string | null
  createProject: (name: string, customInstructions?: string) => Promise<OperationResult<ProjectDisplay>>
  updateProject: (id: string, updates: { name?: string; customInstructions?: string }) => Promise<OperationResult<boolean>>
  deleteProject: (id: string) => Promise<OperationResult<boolean>>
  assignChatToProject: (chatId: string, projectId: string | null) => Promise<OperationResult<boolean>>
  refreshProjects: () => Promise<void>
  getProjectById: (id: string) => ProjectDisplay | undefined
  getRecentProjects: (limit?: number) => ProjectDisplay[]
}

const parseApiError = (err: unknown, fallback: string): string => {
  if (axios.isAxiosError(err) && err.response?.status === 422) {
    const detail = err.response.data?.detail
    if (Array.isArray(detail)) {
      return detail.map((d: any) => d.msg).join('. ')
    }
    if (typeof detail === 'string') return detail
  }
  return fallback
}

const ProjectsContext = createContext<ProjectsContextType | null>(null)

export const ProjectsProvider = ({ children }: { children: ReactNode }) => {
  const [projects, setProjects] = useState<ProjectDisplay[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [hasFetched, setHasFetched] = useState(false)

  // Fetch projects from backend on mount (only once)
  useEffect(() => {
    if (hasFetched) return

    const fetchProjects = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const response = await api.get<Project[]>('/api/projects')
        setProjects(response.data.map(toProjectDisplay))
      } catch (err) {
        console.error('Failed to fetch projects:', err)
        setError('Failed to load projects')
      } finally {
        setIsLoading(false)
        setHasFetched(true)
      }
    }
    fetchProjects()
  }, [hasFetched])

  // Create a new project
  const createProject = useCallback(async (name: string, customInstructions: string = ''): Promise<OperationResult<ProjectDisplay>> => {
    setError(null)
    try {
      const response = await api.post<Project>('/api/projects', {
        name: name.trim(),
        custom_instructions: customInstructions.trim()
      })
      const newProject = toProjectDisplay(response.data)
      setProjects(prev => [newProject, ...prev])
      return { data: newProject, error: null }
    } catch (err) {
      console.error('Failed to create project:', err)
      const parsed = parseApiError(err, 'Failed to create project')
      setError(parsed)
      return { data: null, error: parsed }
    }
  }, [])

  // Update an existing project
  const updateProject = useCallback(async (id: string, updates: { name?: string; customInstructions?: string }): Promise<OperationResult<boolean>> => {
    setError(null)
    try {
      const payload: { name?: string; custom_instructions?: string } = {}
      if (updates.name !== undefined) payload.name = updates.name.trim()
      if (updates.customInstructions !== undefined) payload.custom_instructions = updates.customInstructions.trim()

      const response = await api.patch<Project>(`/api/projects/${id}`, payload)
      const updatedProject = toProjectDisplay(response.data)

      setProjects(prev => prev.map(p => p.id === id ? updatedProject : p))
      return { data: true, error: null }
    } catch (err) {
      console.error('Failed to update project:', err)
      const parsed = parseApiError(err, 'Failed to update project')
      setError(parsed)
      return { data: null, error: parsed }
    }
  }, [])

  // Delete a project
  const deleteProject = useCallback(async (id: string): Promise<OperationResult<boolean>> => {
    setError(null)
    try {
      await api.delete(`/api/projects/${id}`)
      setProjects(prev => prev.filter(p => p.id !== id))
      return { data: true, error: null }
    } catch (err) {
      console.error('Failed to delete project:', err)
      const parsed = parseApiError(err, 'Failed to delete project')
      setError(parsed)
      return { data: null, error: parsed }
    }
  }, [])

  // Assign a chat to a project
  const assignChatToProject = useCallback(async (chatId: string, projectId: string | null): Promise<OperationResult<boolean>> => {
    setError(null)
    try {
      await api.post(`/api/conversations/${chatId}/project`, {
        project_id: projectId
      })
      return { data: true, error: null }
    } catch (err) {
      console.error('Failed to assign chat to project:', err)
      const parsed = parseApiError(err, 'Failed to assign chat to project')
      setError(parsed)
      return { data: null, error: parsed }
    }
  }, [])

  // Refresh projects from server
  const refreshProjects = useCallback(async () => {
    try {
      const response = await api.get<Project[]>('/api/projects')
      setProjects(response.data.map(toProjectDisplay))
    } catch (err) {
      console.error('Failed to refresh projects:', err)
      setError(parseApiError(err, 'Failed to refresh projects'))
    }
  }, [])

  // Get project by ID
  const getProjectById = useCallback((id: string): ProjectDisplay | undefined => {
    return projects.find(p => p.id === id)
  }, [projects])

  // Get recent projects (already sorted by backend)
  const getRecentProjects = useCallback((limit: number = 5): ProjectDisplay[] => {
    return projects.slice(0, limit)
  }, [projects])

  return (
    <ProjectsContext.Provider value={{
      projects,
      isLoading,
      error,
      createProject,
      updateProject,
      deleteProject,
      assignChatToProject,
      refreshProjects,
      getProjectById,
      getRecentProjects
    }}>
      {children}
    </ProjectsContext.Provider>
  )
}

export const useProjects = () => {
  const context = useContext(ProjectsContext)
  if (!context) {
    throw new Error('useProjects must be used within a ProjectsProvider')
  }
  return context
}
